/**
 * Sales Page.
 *
 * Lists all sales orders with status filtering.
 */

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ShoppingCart, Plus, Trash2 } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { useSalesOrders } from "@/features/sales/api";
import { SO_STATUSES } from "@/types/sales-order";
import { toast } from "@/stores/toastStore";
import Badge from "@/components/ui/Badge";
import BulkActionBar from "@/components/ui/BulkActionBar";
import BulkResultPanel from "@/components/BulkResultPanel";
import { useBulkRun, type BulkOutcome } from "@/features/bulk/api";
import Button from "@/components/ui/Button";
import Card from "@/components/ui/Card";
import ConfirmDeleteDialog from "@/components/ui/ConfirmDeleteDialog";
import LoadingSpinner from "@/components/ui/LoadingSpinner";

const statusVariant: Record<
  string,
  "default" | "success" | "warning" | "danger" | "info"
> = {
  Draft: "default",
  Confirmed: "info",
  Invoiced: "warning",
  Paid: "success",
  Cancelled: "danger",
};

function SalesPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<string | undefined>(
    undefined,
  );
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const toggleSelect = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const { data, isLoading } = useSalesOrders({ page, status: statusFilter });

  // One request, and the server reports every row. The loop this replaces fired
  // one `DELETE ?force=true` per selected row and discarded every failure, so a
  // run that half worked said "37 of 50 deleted" and left the user to find out
  // which thirteen by looking at the list. `cascade` is no longer implied:
  // sales orders with related records come back named, in the panel.
  const bulkRun = useBulkRun();
  const [bulkOutcome, setBulkOutcome] = useState<BulkOutcome | null>(null);

  const runBulk = (operation: string, params: Record<string, unknown> = {}) => {
    const ids = [...selected];
    bulkRun.mutate(
      { operation, selection: { ids }, params },
      {
        onSuccess: (outcome) => {
          qc.invalidateQueries({ queryKey: ["sales-orders"] });
          setSelected(new Set());
          setBulkOutcome(outcome.failed > 0 ? outcome : null);
          if (outcome.failed === 0) toast.success(outcome.message);
          else if (outcome.succeeded === 0) toast.error(outcome.message);
          else toast.warning(outcome.message);
        },
        onError: (error: unknown) => {
          const detail =
            (error as { response?: { data?: { detail?: string } } })?.response
              ?.data?.detail ?? "That could not be done.";
          toast.error(detail);
        },
      },
    );
  };

  const fmt = (v: number) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(v);

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-2xl font-bold text-[var(--text-primary)]">
            Sales Orders
          </h2>
          <p className="mt-1 text-sm text-[var(--text-secondary)]">
            Manage sales, invoices, and payments.
          </p>
        </div>
        <Button onClick={() => navigate("/sales/new")}>
          <Plus className="h-4 w-4" /> New Sale
        </Button>
      </div>

      <div className="flex flex-wrap gap-2">
        <button
          onClick={() => {
            setStatusFilter(undefined);
            setPage(1);
          }}
          className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${!statusFilter ? "bg-blue-100 text-[var(--color-interactive-primary)]" : "bg-[var(--surface-sunken)] text-[var(--text-secondary)] hover:bg-[var(--surface-sunken)]/80"}`}
        >
          All
        </button>
        {SO_STATUSES.map((s) => (
          <button
            key={s}
            onClick={() => {
              setStatusFilter(s);
              setPage(1);
            }}
            className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${statusFilter === s ? "bg-blue-100 text-[var(--color-interactive-primary)]" : "bg-[var(--surface-sunken)] text-[var(--text-secondary)] hover:bg-[var(--surface-sunken)]/80"}`}
          >
            {s}
          </button>
        ))}
      </div>

      {isLoading ? (
        <LoadingSpinner className="py-12" />
      ) : data && data.items.length > 0 ? (
        <div className="space-y-2 stagger-children">
          {data.items.map((so) => (
            <Card
              key={so.id}
              padding="none"
              className="cursor-pointer hover-lift"
              onClick={() => navigate(`/sales/${so.id}`)}
            >
              <div className="flex items-stretch">
                <div
                  className="flex items-center pl-3"
                  onClick={(e) => e.stopPropagation()}
                >
                  <input
                    type="checkbox"
                    checked={selected.has(so.id)}
                    onChange={() => toggleSelect(so.id)}
                    onClick={(e) => e.stopPropagation()}
                    className="shrink-0 rounded border-[var(--border-default)]"
                  />
                </div>
                <div className="flex-1 p-4">
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-sm font-semibold text-brand-600">
                      {so.so_number}
                    </span>
                    <Badge variant={statusVariant[so.status] || "default"}>
                      {so.status}
                    </Badge>
                    {so.paid_at && <Badge variant="success">Paid</Badge>}
                  </div>
                  <div className="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-[var(--text-secondary)]">
                    {so.order_date && (
                      <span>
                        Ordered {new Date(so.order_date).toLocaleDateString()}
                      </span>
                    )}
                    {so.payment_method && (
                      <span className="capitalize">
                        Paid via {so.payment_method.replace("_", " ")}
                      </span>
                    )}
                    {so.commission_amount > 0 && (
                      <span>Commission: {fmt(so.commission_amount)}</span>
                    )}
                  </div>
                </div>
                <div className="flex shrink-0 items-center gap-4 border-l border-[var(--color-border-light)] bg-[var(--color-surface-tertiary)] px-5 py-4">
                  {so.tax > 0 && (
                    <div className="text-right">
                      <p className="text-[10px] uppercase tracking-wider text-[var(--text-tertiary)]">
                        Tax
                      </p>
                      <p className="text-xs text-[var(--text-secondary)]">
                        {fmt(so.tax)}
                      </p>
                    </div>
                  )}
                  <div className="text-right">
                    <p className="text-[10px] uppercase tracking-wider text-[var(--text-tertiary)]">
                      Total
                    </p>
                    <p className="text-sm font-bold text-[var(--text-primary)]">
                      {fmt(so.total)}
                    </p>
                  </div>
                </div>
                <div
                  className="flex items-center gap-1 shrink-0 pr-3"
                  onClick={(e) => e.stopPropagation()}
                >
                  <button
                    onClick={() => setDeleteTarget(so.id)}
                    className="p-1.5 text-[var(--text-tertiary)] hover:text-[var(--text-danger)] transition-colors"
                    title="Delete"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      ) : (
        <Card>
          <div className="flex flex-col items-center py-12">
            <div className="empty-state-icon">
              <ShoppingCart />
            </div>
            <h3 className="mt-4 text-lg font-medium text-[var(--text-primary)]">
              No sales orders found
            </h3>
            <Button className="mt-4" onClick={() => navigate("/sales/new")}>
              <Plus className="h-4 w-4" /> New Sale
            </Button>
          </div>
        </Card>
      )}

      <ConfirmDeleteDialog
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        apiPath="/api/v1/sales-orders"
        entityId={deleteTarget || ""}
        queryKey="sales-orders"
      />

      <BulkResultPanel
        outcome={bulkOutcome}
        onClose={() => setBulkOutcome(null)}
        noun="sales orders"
      />

      <BulkActionBar
        selectedCount={selected.size}
        onClearSelection={() => setSelected(new Set())}
        actions={[
          {
            label: "Cancel Selected",
            onClick: () => runBulk("sales_order.cancel"),
            isLoading: bulkRun.isPending,
          },
          {
            label: "Delete Selected",
            onClick: () => runBulk("sales_order.delete", { cascade: true }),
            variant: "danger",
            isLoading: bulkRun.isPending,
          },
        ]}
      />
    </div>
  );
}

export default SalesPage;
